import {DataStatus, Form, FormField, FormSubmitHandler, Search} from 'platform/components';
import {Box, Heading, VStack} from 'platform/foundation';

import {useState, useTransition} from 'react';

import {isEmpty} from 'ramda';

import {
  PermissionScopeResponseBody,
  useGetRoleQuery,
  useListPermissionsV2Query,
  useListProtectedUnitsQuery,
  usePatchRoleMutation,
  usePutPermissionsMutation,
} from '@dms/api/security';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {useNavigate, useRequiredParams} from 'shared/web';

import {AccessControlList} from '../../AccessControlList/AccessControlList';
import {getAllProtectedUnitsDefaultValues} from '../utils/getAllProtectedUnitsDefaultValues';
import {getGrantedProtectedUnits} from '../utils/getGrantedProtectedUnits';
import {getSubmitPermissionsData} from '../utils/getSubmitPermissionsData';

type FormValues = {
  title: string;
  protectedUnits: Record<string, {value: boolean; scopes: PermissionScopeResponseBody[]}>;
};

interface DeferredSearchProps {
  onChange: (value: string | null) => void;
}

function DeferredSearch(props: DeferredSearchProps) {
  const [searchString, setSearchString] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  const handleChange = (value: string | null) => {
    setSearchString(value);
    startTransition(() => {
      props.onChange(value);
    });
  };

  return <Search value={searchString} onChange={handleChange} />;
}

export function RoleDetail() {
  const {roleId} = useRequiredParams();
  const [deferredSearch, setDeferredSearch] = useState<string | null>(null);

  const navigate = useNavigate();

  const {data: role, isLoading: isLoadingRole, isError: isRoleError} = useGetRoleQuery({roleId});

  const {
    data: permissions,
    isLoading: isLoadingPermissions,
    isError: isErrorPermissions,
  } = useListPermissionsV2Query({
    roleId,
  });

  const {
    data: resources,
    isLoading: isLoadingResources,
    isError: isResourcesError,
  } = useListProtectedUnitsQuery();

  const isLoading = isLoadingPermissions || isLoadingResources || isLoadingRole;
  const isError = isErrorPermissions || isResourcesError || isRoleError;

  const [updateRoleMutation] = usePatchRoleMutation();
  const [updatePermissionsMutation] = usePutPermissionsMutation();

  const defaultValues: FormValues = {
    title: role?.title || '',
    protectedUnits: {
      ...getAllProtectedUnitsDefaultValues(resources),
      ...getGrantedProtectedUnits(permissions),
    },
  };

  const handleUpdateRole: FormSubmitHandler<FormValues> = async (
    {title, protectedUnits},
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const updateRole = updateRoleMutation({
      roleId,
      patchRoleRequestBody: {title},
    })
      .unwrap()
      .catch(handleApiError);

    const updatePermissions = updatePermissionsMutation({
      roleId,
      permissions: getSubmitPermissionsData(protectedUnits),
    })
      .unwrap()
      .catch(handleApiError);

    await Promise.all([updateRole, updatePermissions]).then(() => {
      if (blockerContext?.wasSubmittedFromBlocker) {
        return;
      }
      navigate(settingsRoutes.roleManagement);
    });
  };

  return (
    <DataStatus
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmpty(resources)}
      minHeight={200}
    >
      <Form<FormValues>
        defaultValues={defaultValues}
        onSubmit={handleUpdateRole}
        shouldWatchForUnsavedChanges
      >
        {(control, _formApi, navigateWithoutBlocker) => {
          const handleDiscard = () => navigateWithoutBlocker(settingsRoutes.roleManagement);

          return (
            <VStack spacing={5}>
              <Box maxWidth={100}>
                <FormField
                  control={control}
                  name="title"
                  type="text"
                  isDisabled={role?.system}
                  label={i18n.t('page.settings.labels.roleName')}
                  data-testid={testIds.settings.roleManagementDetail('roleName')}
                />
              </Box>

              <Heading size={4}>{i18n.t('page.settings.role.tabs.label.permissions')}</Heading>
              <Box width={60}>
                <DeferredSearch onChange={setDeferredSearch} />
              </Box>

              <AccessControlList
                roleId={roleId}
                searchString={deferredSearch}
                resources={resources}
              />

              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.discard'),
                    variant: 'secondary',
                    onClick: handleDiscard,
                  },
                  {
                    type: 'form-button',
                    control,
                    title: i18n.t('general.actions.saveChanges'),
                    buttonType: 'submit',
                    isDisabled: role?.system,
                  },
                ]}
              />
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
